import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Check,
  FilePlus,
  Plus,
  ReceiptText,
  Save,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePermissions } from '@/hooks/usePermissions'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  FORM_DATE_FIELD_CLASS,
  FORM_TEXTAREA_CLASS,
} from '@/components/ui/form-control-styles'
import { Skeleton } from '@/components/ui/skeleton'
import { DatePicker } from '@/components/shared/DatePicker'
import {
  confirmBrandBtnClass,
  confirmDeleteCancelBtnClass,
  confirmDeleteDialogContentClass,
} from '@/components/shared/ConfirmDeleteDialog'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import {
  useCancelJob,
  useCloseJob,
  useDeleteJob,
  useJob,
  useReassignJob,
  useRescheduleJob,
  useStartJob,
} from '@/hooks/useJobs'
import { todayIsoDate } from '@/lib/date'
import { AssignTechnicianSection } from '@/components/jobs/AssignTechnicianSection'
import {
  JOB_DELETE_SCOPE,
  JOB_STATUS,
  type DeleteJobScope,
  type JobDetail,
  type Technician,
} from '@/types'

const jobDialogTitleClass =
  'font-heading text-base font-semibold leading-[1.2] text-[#171a20]'
const jobDialogBodyClass = 'text-sm font-normal leading-[1.4] text-[#171a20]'
const jobDialogLabelClass = 'text-sm font-normal leading-[1.4] text-[#171a20]'
const jobSectionHeadingClass =
  'mb-2 flex border-l-4 border-[#ff5a1f] pl-4 font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'

// 2.3.11 Delete job
export function DeleteJobDialog({
  job,
  open,
  onOpenChange,
}: {
  job: JobDetail
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const navigate = useNavigate()
  const { t } = useTranslation('jobs')
  const del = useDeleteJob()
  const isRecurring =
    !!job.repeat_schedule_id && job.status !== JOB_STATUS.to_plan
  const [scope, setScope] = useState<DeleteJobScope>(
    JOB_DELETE_SCOPE.this_occurrence
  )

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setScope(JOB_DELETE_SCOPE.this_occurrence)
    }
    onOpenChange(next)
  }

  const confirm = async () => {
    const deletingSingle =
      !isRecurring || scope === JOB_DELETE_SCOPE.this_occurrence
    if (deletingSingle && job.status === JOB_STATUS.in_progress) {
      toast({
        variant: 'destructive',
        title: t('dialogs.cannotDeleteInProgress'),
      })
      return
    }
    if (deletingSingle && job.status === JOB_STATUS.completed) {
      toast({
        variant: 'destructive',
        title: t('dialogs.cannotDeleteCompleted'),
      })
      return
    }
    try {
      const res = await del.mutateAsync({
        id: job.id,
        scope: isRecurring ? scope : undefined,
      })
      toast({
        variant: 'success',
        title: res.message || t('dialogs.deleteSuccess'),
      })
      navigate('/calendar')
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('dialogs.deleteError')),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={confirmDeleteDialogContentClass}
        overlayClassName='bg-black/70 backdrop-blur-[2px]'
        showCloseButton={false}
      >
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-3'>
            <DialogTitle className={jobDialogTitleClass}>
              {t('dialogs.deleteTitle')}
            </DialogTitle>
            <p className={jobDialogBodyClass}>{t('dialogs.deleteConfirm')}</p>
          </div>

          {isRecurring && (
            <fieldset className='flex flex-col gap-3'>
              <legend className='sr-only'>
                {t('dialogs.deleteScopeLegend')}
              </legend>
              {(
                [
                  {
                    value: JOB_DELETE_SCOPE.this_occurrence,
                    label: t('dialogs.deleteThisOccurrence'),
                  },
                  {
                    value: JOB_DELETE_SCOPE.all_future_occurrences,
                    label: t('dialogs.deleteAllFuture'),
                  },
                ] as const
              ).map(opt => {
                const active = scope === opt.value
                return (
                  <button
                    key={opt.value}
                    type='button'
                    aria-pressed={active}
                    onClick={() => setScope(opt.value)}
                    className={cn(
                      'flex items-center gap-3 rounded-xl border p-4 text-left transition-colors',
                      active
                        ? 'border-[#ff5a1f] bg-[#fff1ea]'
                        : 'border-input hover:bg-accent/40'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                        active
                          ? 'border-[#ff5a1f]'
                          : 'border-muted-foreground/40'
                      )}
                    >
                      {active && (
                        <span className='h-2.5 w-2.5 rounded-full bg-[#ff5a1f]' />
                      )}
                    </span>
                    <span className='flex-1 text-sm font-normal leading-[1.4] text-[#171a20]'>
                      {opt.label}
                    </span>
                  </button>
                )
              })}
            </fieldset>
          )}

          <div className='flex items-center justify-end gap-4'>
            <Button
              variant='outline'
              className={cn(confirmDeleteCancelBtnClass, 'h-11 text-sm')}
              onClick={() => handleOpenChange(false)}
              disabled={del.isPending}
            >
              {t('dialogs.cancel')}
            </Button>
            <Button
              className={cn(confirmBrandBtnClass, 'h-11 text-sm')}
              onClick={confirm}
              disabled={del.isPending}
            >
              {t('dialogs.confirmDelete')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// 2.3.14 Cancel job
export function CancelJobDialog({
  job,
  open,
  onOpenChange,
}: {
  job: JobDetail
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation('jobs')
  const cancel = useCancelJob(job.id)
  const [reason, setReason] = useState('')

  const handleOpenChange = (next: boolean) => {
    if (!next) setReason('')
    onOpenChange(next)
  }

  const confirm = async () => {
    try {
      await cancel.mutateAsync(reason)
      toast({ variant: 'success', title: t('dialogs.cancelSuccess') })
      handleOpenChange(false)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('dialogs.cancelError')),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className={confirmDeleteDialogContentClass}
        showCloseButton={false}
      >
        <div className='flex flex-col gap-6'>
          <DialogTitle className={cn('pr-8', jobDialogTitleClass)}>
            {t('dialogs.cancelTitle')}
          </DialogTitle>

          <div className='flex flex-col gap-2'>
            <label
              htmlFor={`cancel-job-reason-${job.id}`}
              className={jobDialogLabelClass}
            >
              {t('dialogs.cancellationReason')}
            </label>
            <Textarea
              id={`cancel-job-reason-${job.id}`}
              rows={4}
              maxLength={500}
              value={reason}
              onChange={e => setReason(e.target.value)}
              disabled={cancel.isPending}
              className={cn(
                'min-h-[120px] resize-none disabled:opacity-60',
                FORM_TEXTAREA_CLASS
              )}
            />
          </div>

          <div className='flex items-center justify-end gap-4'>
            <Button
              variant='outline'
              className={confirmDeleteCancelBtnClass}
              onClick={() => handleOpenChange(false)}
              disabled={cancel.isPending}
            >
              {t('dialogs.back')}
            </Button>
            <Button
              className={confirmBrandBtnClass}
              onClick={confirm}
              disabled={cancel.isPending || !reason.trim()}
            >
              {t('dialogs.confirmCancellation')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

type CallbackTime = '00:00' | '08:00' | '14:00'

const CALLBACK_TIMES: CallbackTime[] = ['08:00', '14:00', '00:00']

// CR 86eyqf2nt — capture the next callback and atomically return an assigned
// intervention to the To plan queue. Time is optional and defaults to Other
// (00:00); no manual time input is shown.
export function RescheduleJobDrawerContent({
  jobId,
  onClose,
}: {
  jobId: string
  onClose: () => void
}) {
  const { t } = useTranslation('jobs')
  const { data, isLoading, isError } = useJob(jobId)
  const reschedule = useRescheduleJob(jobId)
  const [callbackDate, setCallbackDate] = useState('')
  const [callbackTime, setCallbackTime] = useState<CallbackTime | null>(null)
  const [dateError, setDateError] = useState(false)

  const confirm = async () => {
    if (!callbackDate) {
      setDateError(true)
      return
    }

    try {
      await reschedule.mutateAsync({
        callback_date: callbackDate,
        callback_time: callbackTime ?? '00:00',
      })
      toast({ variant: 'success', title: t('dialogs.reschedule.success') })
      onClose()
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('dialogs.reschedule.error')),
      })
    }
  }

  if (isLoading) {
    return (
      <div className='flex h-full flex-col gap-4 bg-[#f4f5f8] p-6'>
        <Skeleton className='h-8 w-56 rounded-lg' />
        <Skeleton className='h-80 w-full rounded-2xl' />
        <Skeleton className='h-24 w-full rounded-2xl' />
      </div>
    )
  }

  const job = data?.data
  if (isError || !job) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-4 bg-[#f4f5f8] p-6'>
        <p className='text-sm text-destructive'>{t('detail.notFound')}</p>
        <Button variant='outline' onClick={onClose}>
          <ArrowLeft className='mr-2 size-4' strokeWidth={1.6} />
          {t('report.backToIntervention')}
        </Button>
      </div>
    )
  }

  const previousTime =
    job.start_time && job.end_time
      ? `${job.start_time} - ${job.end_time}`
      : t('agenda.monthHover.noTime')

  return (
    <div className='flex min-h-full flex-col gap-4 bg-[#f4f5f8] p-6'>
      <Button
        variant='outline'
        className='h-8 w-fit rounded-lg border-[#e7e9ef] bg-white px-2 text-sm font-medium leading-none text-[#171a20] hover:bg-white'
        onClick={onClose}
        disabled={reschedule.isPending}
      >
        <ArrowLeft className='mr-2 size-4' strokeWidth={1.6} />
        {t('report.backToIntervention')}
      </Button>

      <div className='overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'>
        <div className='flex flex-col gap-2 border-b border-[#e7e9ef] p-6 text-left'>
          <span className='w-fit rounded-lg bg-[#fef3c7] px-2 py-1 text-xs font-medium leading-4 text-[#8a5405]'>
            {t('dialogs.reschedule.toPlan')}
          </span>
          <h2 className={jobDialogTitleClass}>
            {t('dialogs.reschedule.title')}
          </h2>
          <p className='text-sm font-normal leading-[1.4] text-[#6c7285]'>
            {job.reference ??
              t('detail.jobFallback', { id: job.id.slice(0, 8) })}
            <span className='px-2'>•</span>
            {previousTime}
          </p>
        </div>

        <div className='space-y-4 p-6'>
          <div>
            <label className={jobSectionHeadingClass}>
              {t('dialogs.reschedule.callbackDate')}
            </label>
            <DatePicker
              value={callbackDate}
              onChange={value => {
                setCallbackDate(value)
                if (value) setDateError(false)
              }}
              min={todayIsoDate()}
              error={dateError}
              variant='field'
              className={FORM_DATE_FIELD_CLASS}
            />
            {dateError && (
              <p className='mt-1 text-xs text-destructive'>
                {t('dialogs.reschedule.required')}
              </p>
            )}
          </div>

          <fieldset>
            <legend className={cn('w-full', jobSectionHeadingClass)}>
              {t('dialogs.reschedule.callbackTime')}
            </legend>
            <div className='flex flex-wrap gap-2 rounded-2xl bg-[#f4f5f8] p-4'>
              {CALLBACK_TIMES.map(time => {
                const selected = callbackTime === time
                return (
                  <button
                    key={time}
                    type='button'
                    aria-pressed={selected}
                    onClick={() => setCallbackTime(time)}
                    className={cn(
                      'inline-flex h-10 items-center gap-2 rounded-xl border bg-white px-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.05)] transition',
                      time === '00:00' && !selected
                        ? 'border-dashed border-[#b0b5c3] text-[#3d434e]'
                        : 'border-[#e7e9ef]',
                      selected &&
                        'border-[#10b981] bg-[#ecfdf5] ring-1 ring-[#10b981]'
                    )}
                  >
                    {selected ? (
                      <Check className='size-4 text-[#10b981]' />
                    ) : time === '00:00' ? (
                      <Plus className='size-4' />
                    ) : null}
                    {t(`dialogs.reschedule.times.${time.replace(':', '')}`)}
                  </button>
                )
              })}
            </div>
          </fieldset>
        </div>
      </div>

      <div className='flex justify-end gap-4 rounded-2xl border border-[#e7e9ef] bg-white p-6 shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'>
        <Button
          variant='outline'
          className='h-10 rounded-xl px-3 text-sm font-medium leading-none'
          onClick={onClose}
          disabled={reschedule.isPending}
        >
          {t('dialogs.cancel')}
        </Button>
        <Button
          className='h-10 rounded-xl bg-[#ff5a1f] px-3 text-sm font-medium leading-none hover:bg-[#e94e14]'
          onClick={confirm}
          disabled={reschedule.isPending}
        >
          <Check className='mr-2 size-4' />
          {t('dialogs.confirm')}
        </Button>
      </div>
    </div>
  )
}

// 2.3.13 Start / Close intervention
export function StartCloseDialog({
  job,
  mode,
  open,
  onOpenChange,
  onClosed,
}: {
  job: JobDetail
  mode: 'start' | 'close'
  open: boolean
  onOpenChange: (o: boolean) => void
  // 2.3.18 - invoked after a successful close instead of the default
  // navigate-to-report, so the parent can open the Post-Job Actions modal.
  onClosed?: () => void
}) {
  const { t } = useTranslation('jobs')
  const navigate = useNavigate()
  const { canEdit } = usePermissions()
  const canEditAgenda = canEdit('agenda')
  const start = useStartJob(job.id)
  const close = useCloseJob(job.id)

  const confirm = async () => {
    try {
      if (mode === 'start') {
        await start.mutateAsync()
        toast({ variant: 'success', title: t('dialogs.startSuccess') })
        onOpenChange(false)
      } else {
        await close.mutateAsync()
        toast({ variant: 'success', title: t('dialogs.closeSuccess') })
        onOpenChange(false)
        if (onClosed) {
          // 2.3.18 - present the Post-Job Actions modal (Create quote / Create
          // report & collect payment) rather than jumping straight to a report.
          onClosed()
        } else if (canEditAgenda) {
          // Feedback #90 fallback - closing a job triggers Report Creation.
          navigate(`/jobs/${job.id}/report/new`)
        }
      }
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(
          e,
          mode === 'start' ? t('dialogs.startError') : t('dialogs.closeError')
        ),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={confirmDeleteDialogContentClass}
        overlayClassName='bg-black/70 backdrop-blur-[2px]'
      >
        <DialogHeader>
          <DialogTitle className={jobDialogTitleClass}>
            {mode === 'start'
              ? t('dialogs.confirmStart')
              : t('dialogs.confirmClose')}
          </DialogTitle>
        </DialogHeader>
        <p className={jobDialogBodyClass}>
          {mode === 'start'
            ? t('dialogs.startPrompt')
            : t('dialogs.closePrompt')}
        </p>
        <DialogFooter>
          <Button
            variant='outline'
            className={confirmDeleteCancelBtnClass}
            onClick={() => onOpenChange(false)}
          >
            {t('dialogs.cancel')}
          </Button>
          <Button
            className={confirmBrandBtnClass}
            onClick={confirm}
            disabled={start.isPending || close.isPending}
          >
            {t('dialogs.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// 2.3.12 Reassign technician
export function ReassignDialog({
  job,
  open,
  onOpenChange,
}: {
  job: JobDetail
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation('jobs')
  const reassign = useReassignJob(job.id, job.client_id)
  const [techs, setTechs] = useState<Technician[]>(
    job.assigned_technicians ?? []
  )

  const save = async () => {
    try {
      await reassign.mutateAsync(techs.map(tech => tech.id))
      toast({ variant: 'success', title: t('dialogs.reassignSuccess') })
      onOpenChange(false)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('dialogs.reassignError')),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='flex max-h-[calc(100dvh-32px)] w-[calc(100vw-32px)] max-w-[863px] flex-col gap-6 overflow-hidden rounded-2xl border-[#e7e9ef] bg-white p-4 shadow-[0_8px_16px_-8px_rgba(38,45,66,0.1),0_1px_2px_rgba(38,45,66,0.05)] sm:rounded-2xl sm:p-8'
        overlayClassName='bg-black/70 backdrop-blur-[2px]'
        showCloseButton={false}
        // Don't auto-focus the search input on open - focusing it fires its
        // onFocus and immediately expands the suggestions dropdown.
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className={jobDialogTitleClass}>
            {t('dialogs.reassignTitle')}
          </DialogTitle>
        </DialogHeader>
        <div className='min-h-0 flex-1 overflow-hidden pr-1 pl-1'>
          <AssignTechnicianSection
            selected={techs}
            onChange={setTechs}
            layout='reassign'
            context={{
              quote_id: job.quote_id || undefined,
              contract_id: job.contract_id || undefined,
              service_type: job.service_type || undefined,
              intervention_type: job.intervention_type || undefined,
              scheduled_date: job.scheduled_date?.slice(0, 10) || undefined,
              start_time: job.start_time || undefined,
              end_time: job.end_time || undefined,
              priority: job.priority,
              latitude: job.latitude ?? undefined,
              longitude: job.longitude ?? undefined,
              // Exclude this job so a technician already on it is not counted
              // busy against their own assignment here.
              exclude_job_id: job.id,
            }}
          />
        </div>
        <DialogFooter className='shrink-0 gap-4 sm:space-x-0'>
          <Button
            variant='outline'
            className='h-[42px] rounded-xl border-[#e7e9ef] bg-white p-3 text-sm font-medium leading-none text-[#171a20] hover:bg-[#f4f5f8]'
            onClick={() => onOpenChange(false)}
          >
            {t('dialogs.cancel')}
          </Button>
          <Button
            className='h-[42px] gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] p-3 text-sm font-medium leading-none text-white hover:bg-[#e94e14]'
            onClick={save}
            disabled={reassign.isPending}
          >
            <Save className='size-4 shrink-0' />
            {t('dialogs.saveAssignment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// 2.3.18 / 2.3.19 Complete Intervention - shown once a job reaches Completed
// (after Close intervention, or re-openable from the completed job detail). The
// operator selects one of two back-office continuation paths and confirms:
//   - Create a post-inspection quote (2.3.18)
//   - Create the report and collect payment (2.3.19)
// Per BR12 the *downstream* payment behaviour (amount source, whether a payment
// is collected at all) is driven by the job's payment option - this modal only
// selects the path. When the job's payment option is "no_collection" the second
// path is report-only, so its label drops the "payment" wording.
type PostJobPath = 'quote' | 'payment'

export function PostJobActionsDialog({
  job,
  open,
  onOpenChange,
}: {
  job: JobDetail
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation('jobs')
  const navigate = useNavigate()
  const { canEdit } = usePermissions()
  const canEditAgenda = canEdit('agenda')
  const canEditQuotes = canEdit('quotes')
  const noCollection =
    job.payment_collection_required === false ||
    job.payment_option === 'no_collection'
  const wonVisitQuote =
    job.intervention_type === 'visit_quote'
      ? job.quote_reference?.status === 'won'
        ? job.quote_reference
        : job.quotes?.find(quote => quote.status === 'won')
      : undefined
  // Figma default: the post-inspection quote path is preselected when available.
  const [path, setPath] = useState<PostJobPath>(
    canEditQuotes ? 'quote' : 'payment'
  )

  const options = useMemo(() => {
    const next: { value: PostJobPath; label: string; icon: ReactNode }[] = []
    if (canEditQuotes && !wonVisitQuote) {
      next.push({
        value: 'quote',
        label: t('dialogs.postJob.createQuote'),
        icon: <FilePlus className='h-4 w-4' />,
      })
    }
    if (canEditAgenda) {
      next.push({
        value: 'payment',
        label: noCollection
          ? t('dialogs.postJob.createReportOnly')
          : t('dialogs.postJob.createReport'),
        icon: <ReceiptText className='h-4 w-4' />,
      })
    }
    return next
  }, [canEditAgenda, canEditQuotes, noCollection, t, wonVisitQuote])

  const selectedPath =
    options.find(opt => opt.value === path)?.value ?? options[0]?.value

  // Resolve the destination for the selected path (no side effects, so it can
  // run before the dialog closes).
  const resolveTarget = (): string => {
    // Standalone post-inspection quote (2.3.18): create the quote and stop; no
    // payment, no report. Pre-fill the client and link the quote back to this
    // job (2.5.2 + 2.3.18).
    if (selectedPath === 'quote') {
      return `/quotes/new?client_id=${job.client_id}&job_id=${job.id}`
    }

    // [2.3.19] Payment-first flow (BA flow): collect payment BEFORE the 3-step
    // report wizard, which is always the final step.
    //  - no_collection  -> no payment; straight to the report wizard.
    //  - quote_on_site   -> create the quote first (its total becomes the amount),
    //                       then payment, then report (chained via query params).
    //  - to_collect      -> payment prefilled with the portal amount, then report.
    //  - effort_based / no option -> payment with an empty amount, then report.
    if (noCollection) {
      return `/jobs/${job.id}/report/new`
    }

    const existingQuote = wonVisitQuote ?? job.quotes?.[0]
    if (job.payment_option === 'quote_on_site' && !existingQuote) {
      // QuoteForm hands off to /payments/new (amount = quote total), which in
      // turn hands off to the report wizard once the payment is recorded.
      return `/quotes/new?client_id=${job.client_id}&job_id=${job.id}&collect_job=${job.id}`
    }

    const params = new URLSearchParams({
      client_id: job.client_id,
      job_id: job.id,
      // After the payment is recorded, continue to this job's report wizard.
      report_job: job.id,
    })
    const amount =
      job.payment_option === 'to_collect'
        ? job.amount_to_collect
        : job.payment_option === 'quote_on_site'
          ? existingQuote?.total
          : undefined
    if (amount != null) params.set('amount', String(amount))
    return `/payments/new?${params.toString()}`
  }

  const confirm = () => {
    if (!selectedPath) {
      onOpenChange(false)
      return
    }
    const target = resolveTarget()
    onOpenChange(false)
    // Defer navigation to the next tick. Navigating synchronously unmounts this
    // still-open Radix dialog with the route change, which leaves
    // `pointer-events: none` stuck on <body> and freezes the destination page.
    // Closing first lets Radix restore body pointer-events before we navigate.
    setTimeout(() => navigate(target), 0)
  }

  return (
    <Dialog open={open && options.length > 0} onOpenChange={onOpenChange}>
      {/* Completing the intervention is mandatory when paths exist. Hide the
          close (X), and block outside-click / Escape so the modal can only be
          dismissed by Confirm. */}
      <DialogContent
        className='[&>button]:hidden'
        onInteractOutside={options.length ? e => e.preventDefault() : undefined}
        onEscapeKeyDown={options.length ? e => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle className={jobDialogTitleClass}>
            {t('dialogs.postJob.title')}
          </DialogTitle>
        </DialogHeader>
        <div className='flex flex-col gap-3'>
          {options.map(opt => {
            const active = selectedPath === opt.value
            return (
              <button
                key={opt.value}
                type='button'
                aria-pressed={active}
                onClick={() => setPath(opt.value)}
                className={cn(
                  'flex items-center gap-3 rounded-xl border p-4 text-left transition-colors',
                  active
                    ? 'border-[#ff5a1f] bg-[#fff1ea]'
                    : 'border-input hover:bg-accent/40'
                )}
              >
                <span className='text-[#ff5a1f]'>{opt.icon}</span>
                <span className='flex-1 text-sm font-normal leading-[1.4] text-[#171a20]'>
                  {opt.label}
                </span>
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                    active ? 'border-[#ff5a1f]' : 'border-muted-foreground/40'
                  )}
                >
                  {active && (
                    <span className='h-2.5 w-2.5 rounded-full bg-[#ff5a1f]' />
                  )}
                </span>
              </button>
            )
          })}
        </div>
        <DialogFooter>
          <Button className={confirmBrandBtnClass} onClick={confirm}>
            {t('dialogs.postJob.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
